import PropTypes from 'prop-types';
import { useEffect } from 'react';
/** @jsx jsx this comment is required for Storybook to render */
import { css, jsx, keyframes } from '@emotion/core';
import * as THREE from 'three';
import TextBlock from '../../molecules/TextBlock/TextBlock';
import PageWrapper from '../PageWrapper/PageWrapper';
import Section from '../../molecules/Section/Section';
import {
  breakpoints,
  fontFamilies,
  unitConverter as uc,
} from '../../../styles/base';

// for the AI Banner
import binary1Img from './assets/binary-1.png';
import binary2Img from './assets/binary-2.png';
import binary3Img from './assets/binary-3.png';
import binary4Img from './assets/binary-4.png';
import personOne from './assets/person-bubble-1.png';
import personTwo from './assets/person-bubble-2.png';
import personThree from './assets/person-bubble-3.png';
import personFour from './assets/person-bubble-4.png';
import personFive from './assets/person-bubble-5.png';
import koralBubble from './assets/koral-bubble.png';
import marigoldBubble from './assets/marigold-bubble.png';
import marigoldBubble2 from './assets/marigold-bubble-2.png';
import aquaBubble from './assets/aqua-bubble.png';

// for section under AI Banner
import womanAdjusting from './assets/woman-adjusting-bubbles.png';

const AiBanner = ({
  aiBannerHeading,
  aiBannerText,
  className,
  dataSectionHeading,
  dataSectionText,
  sectionStyles,
}) => {
  useEffect(() => {
    const heroBanner = document.querySelector('.hero-banner');
    let w = heroBanner.clientWidth;
    let h = heroBanner.clientHeight;
    const dpr = window.devicePixelRatio;

    const fov = 60;
    const fovRad = fov * (Math.PI / 180);
    const dist = h / 2 / Math.tan(fovRad);

    const clock = new THREE.Clock();
    const pointSize = 4 * dpr;

    const renderer = new THREE.WebGLRenderer();
    renderer.setSize(w, h);
    renderer.setClearColor(new THREE.Color('#fff'), 1.0);
    renderer.setPixelRatio(dpr);

    const container = document.getElementById('webgl-canvas');
    container.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(fov, w / h, 1, dist * 8);
    camera.position.x = 0;
    camera.position.y = 20;
    camera.position.z = 150;

    const scene = new THREE.Scene();

    // TODO: This fog isn't working for some reason
    const fogColor = '#fff'; // white
    const near = 1.8;
    const far = 2;
    scene.fog = new THREE.Fog(fogColor, near, far);
    scene.background = new THREE.Color(fogColor);

    const geo = new THREE.BufferGeometry();
    const positions = [];

    const width = 200 * (w / h);
    const depth = 100;
    const distance = 5;

    for (let x = 0; x < width; x += distance) {
      for (let z = 0; z < depth; z += distance) {
        positions.push(-width / 2 + x, -30, -depth / 2 + z);
      }
    }
    const positionAttribute = new THREE.Float32BufferAttribute(positions, 3);
    geo.setAttribute('position', positionAttribute);

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        u_time: {
          value: 0.0,
        },
        color1: {
          value: new THREE.Color('#66d0b6'),
        },
        color2: {
          value: new THREE.Color('#6a569c'),
        },
        color3: {
          value: new THREE.Color('#f16078'),
        },
        color4: {
          value: new THREE.Color('#6a569c'),
        },
        color5: {
          value: new THREE.Color('#66d0b6'),
        },
        resolution: { type: 'v2', value: new THREE.Vector2(w * dpr, h * dpr) },
        pointSize: { value: pointSize },
      },
      vertexShader: `
        precision highp float;
        #define M_PI 3.1415926535897932384626433832795
        
        uniform float u_time;
        uniform float pointSize;
        
        void main() {
          vec3 p = position;
          p.y += (
             cos(p.x / M_PI * 8.0 + u_time * 1.5) * 15.0 +
             sin(p.z / M_PI * 8.0 + u_time * 1.5) * 15.0 + 
             60.0
           ) ;
          
          gl_PointSize = pointSize;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
        }   
    
    `,
      fragmentShader: `
        precision highp float;
        
        uniform vec3 color1;
        uniform vec3 color2;
        uniform vec3 color3;
        uniform vec3 color4;
        uniform vec3 color5;
        uniform vec2 resolution;
        
        void main() {
          // create circles instead of squares
          if ( length( gl_PointCoord - vec2( 0.5, 0.5 ) ) > 0.475 ) discard;
          
          float x = gl_FragCoord.x;
          float step1 = 0.25;
          float step2 = 0.45;
          float step3 = 0.55;
          float step4 = 0.75;
          float step5 = 1.00;
          
          float mixValue = x / resolution.x;
          
          // create color stops using 'mix', and convert the progress through each 'step'
          // to a value between 0 and 1
          vec3 mixedColor;
          if(mixValue < step1) {
            mixedColor = mix(color1, color2, mixValue / step1);
          } else if (mixValue >= step1 && mixValue < step2) {
            mixedColor = mix(color2, color3, ((mixValue - step1) / (step2 - step1)));
          } else if (mixValue >= step2 && mixValue < step3) {
            mixedColor = color3;
          } else if (mixValue >= step3 && mixValue < step4) {
            mixedColor = mix(color3, color4, ((mixValue - step3) / (step4 - step3)));
          } else {
            mixedColor = mix(color4, color5, ((mixValue - step4) / (step5 - step4)));
          }
          
          gl_FragColor = vec4(mixedColor, 1.0);
        }
    `,
    });

    const mesh = new THREE.Points(geo, mat);
    scene.add(mesh);

    function render() {
      const time = clock.getElapsedTime();
      mesh.material.uniforms.u_time.value = time;
      renderer.render(scene, camera);
      requestAnimationFrame(render);
    }
    render();

    function onWindowResize() {
      w = heroBanner.clientWidth;
      h = heroBanner.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    window.addEventListener('resize', onWindowResize);
  }, []);

  // TODO: figure out better way of centering
  const sectionCss = css`
    position: relative;
    width: 100vw;
    height: 80vh;
    margin-bottom: ${uc('50px')};
    padding-top: ${uc('160px')};
    overflow: hidden;
    text-align: center;

    video {
      position: absolute;
      top: -10vw;
      left: 0;
      z-index: 0;
    }

    canvas {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
    }

    @media (${breakpoints.mobile}) {
      height: auto;
    }
  `;

  const bubbleKeyframes = keyframes`
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(${uc('20px')});
    }
  `;

  const bubbleContainerCss = css`
    position: relative;
    display: grid;
    justify-items: center;
    grid-template-areas:
      'koral . person-four . logo logo . person-five marigold-2 person-two'
      '. person-three . marigold-1 logo logo . person-one . aqua';

    @media (${breakpoints.mobile}) {
      left: -50vw;
      width: 200vw;
    }
  `;

  const innovationCss = css`
    position: relative;
    grid-area: logo;
    grid-column-end: 11;
    grid-column-start: 1;
    width: ${uc('180px')};
    margin-bottom: ${uc('40px')};
  `;

  const bubbleCss = css`
    animation: ${bubbleKeyframes} 4s infinite;
  `;

  const personOneCss = css`
    grid-area: person-one;
    width: ${uc('120px')};
    ${bubbleCss}
  `;

  const personTwoCss = css`
    grid-area: person-two;
    width: ${uc('95px')};
    ${bubbleCss}
  `;

  const personThreeCss = css`
    grid-area: person-three;
    width: ${uc('84px')};
    ${bubbleCss}
  `;

  const personFourCss = css`
    grid-area: person-four;
    width: ${uc('71px')};
    ${bubbleCss}
  `;

  const personFiveCss = css`
    grid-area: person-five;
    width: ${uc('53px')};
    justify-self: self-start;
    ${bubbleCss}
    @media (${breakpoints.mobile}) {
      justify-self: self-end;
    }
  `;

  const koralBubbleCss = css`
    grid-area: koral;
    width: ${uc('16px')};
    ${bubbleCss}
  `;

  const marigoldBubbleCss = css`
    grid-area: marigold-1;
    width: ${uc('31px')};
    ${bubbleCss}
  `;

  const marigoldBubble2Css = css`
    grid-area: marigold-2;
    width: ${uc('30px')};
    ${bubbleCss}
  `;

  const aquaBubbleCss = css`
    grid-area: aqua;
    align-self: self-end;
    width: ${uc('16px')};
    ${bubbleCss}
  `;

  const subheadingCss = css`
    position: relative;
    display: inline-block;
    max-width: ${uc('600px')};
    margin: ${uc('0 auto 20px')};
    font-size: ${uc('64px')};
    font-family: ${fontFamilies.proximaSoft};
    line-height: 1.1;
    background: linear-gradient(to right, #3bb3e5, #66d0b6, #1249b3, #f16078);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    @media (${breakpoints.mobile}) {
      font-size: ${uc('40px')};
    }
  `;

  const section2Css = css`
    position: relative;
    min-height: ${uc('472px')};

    @media (${breakpoints.mobile}) {
      top: ${uc('20px')};
      height: auto;
    }
  `;

  const textBlockCss = css`
    position: relative;
    justify-content: center;
    max-width: ${uc('680px')};
    text-align: center;

    h2 {
      padding: 0 ${uc('20px')};
    }
  `;

  const bannerTextBlockCss = css`
    ${textBlockCss}
  `;

  const binaryTextBlockCss = css`
    ${textBlockCss}
    top: ${uc('180px')};

    @media (${breakpoints.mobile}) {
      top: 0;
    }
  `;

  const binary1Keyframes = keyframes`
    0% {
      opacity: 0;
    }     
    20%, 70% {
      opacity: 100%;  
    }
    80% {
      opacity: 0%;      
    }
    100% {
      opacity: 0%;
    }
  `;

  const binary2Keyframes = keyframes`
    0% {
      opacity: 0;
    }     
    40%, 70% {
      opacity: 100%;  
    }
    85% {
      opacity: 0%;      
    }
    100% {
      opacity: 0%;
    }
  `;

  const binary3Keyframes = keyframes`
    0% {
      opacity: 0;
    }     
    55%, 70% {
      opacity: 100%;  
    }
    90% {
      opacity: 0%;      
    }
    100% {
      opacity: 0%;
    }
  `;

  const binary4Keyframes = keyframes`
    0% {
      opacity: 0;
    }     
    65%, 70% {
      opacity: 100%;  
    }
    90% {
      opacity: 0%;      
    }
    100% {
      opacity: 0%;
    }
  `;

  const binaryCss = css`
    position: absolute;
    z-index: 0;
    opacity: 0;
    animation: ${binary1Keyframes} 7s infinite;
  `;

  const binary1Css = css`
    ${binaryCss}
    top: ${uc('-240px')};
    left: calc(50% + ${uc('380px')});
    width: ${'400px'};
    animation-name: ${binary1Keyframes};
  `;
  const binary2Css = css`
    ${binaryCss}
    top: ${uc('-80px')};
    left: calc(50% - ${uc('650px')});
    width: ${uc('500px')};
    animation-name: ${binary2Keyframes};
  `;
  const binary3Css = css`
    ${binaryCss}
    top: -10px;
    left: calc(50% + ${uc('220px')});
    width: ${uc('600px')};
    animation-name: ${binary3Keyframes};
  `;
  const binary4Css = css`
    ${binaryCss}
    top: ${uc('-84px')};
    left: calc(50% - ${uc('796px')});
    width: ${uc('450px')};
    animation-name: ${binary4Keyframes};
  `;

  const binaryPeopleCss = css`
    position: absolute;
    right: 0;
    left: 0;
    width: ${uc('1400px')};
    margin: auto;

    @media (${breakpoints.mobile}) {
      top: ${uc('-30px')};
      right: 0;
      left: ${uc('-60px')};
      z-index: 1;
      margin: auto;
    }
  `;

  const bannerTextBlockProps = {
    description: aiBannerText,
  };

  const binaryTextBlockProps = {
    headingText: dataSectionHeading,
    description: dataSectionText,
  };

  return (
    <Section sectionStyles={sectionStyles}>
      <section css={sectionCss} className={`hero-banner ${className}`}>
        <div id="webgl-canvas" />
        <div css={bubbleContainerCss} className="bubbles">
          <img css={personOneCss} src={personOne} alt="Smiling woman" />
          <img css={personTwoCss} src={personTwo} alt="Smiling older man" />
          <img css={personThreeCss} src={personThree} alt="Smiling woman" />
          <img css={personFourCss} src={personFour} alt="Smiling man" />
          <img
            css={personFiveCss}
            src={personFive}
            alt="Smiling woman in headscarf"
          />
          <img
            css={koralBubbleCss}
            src={koralBubble}
            alt="coral colored bubble"
          />
          <img
            css={marigoldBubbleCss}
            src={marigoldBubble}
            alt="marigold colored bubble"
          />
          <img
            css={marigoldBubble2Css}
            src={marigoldBubble2}
            alt="marigold colored bubble"
          />
          <img
            css={aquaBubbleCss}
            src={aquaBubble}
            alt="aqua blue colored bubble"
          />
          <img
            css={innovationCss}
            className="innovation-logo"
            alt="Cornerstone AI Logo"
            src="/csod-ilab-ai-logo-blue-v-01.svg"
          />
        </div>
        <PageWrapper>
          <h1 css={subheadingCss}>{aiBannerHeading}</h1>
          <TextBlock css={bannerTextBlockCss} {...bannerTextBlockProps} />
        </PageWrapper>
      </section>
      <section css={section2Css}>
        <img src={binary1Img} css={binary1Css} alt="floating binary numbers" />
        <img src={binary2Img} css={binary2Css} alt="floating binary numbers" />
        <img src={binary3Img} css={binary3Css} alt="floating binary numbers" />
        <img src={binary4Img} css={binary4Css} alt="floating binary numbers" />
        <img
          src={womanAdjusting}
          css={binaryPeopleCss}
          alt="Drawing of woman adjusting people bubbles"
        />
        <TextBlock css={binaryTextBlockCss} {...binaryTextBlockProps} />
      </section>
    </Section>
  );
};

AiBanner.propTypes = {
  aiBannerHeading: PropTypes.string.isRequired,
  aiBannerText: PropTypes.string,
  className: PropTypes.string,
  dataSectionHeading: PropTypes.string.isRequired,
  dataSectionText: PropTypes.string,
  sectionStyles: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]),
};

AiBanner.defaultProps = {
  aiBannerText: '',
  className: '',
  dataSectionText: '',
  sectionStyles: {},
};

export default AiBanner;
